import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Plus, Search, Download } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import {
  Button,
  Badge,
  Input,
  Skeleton,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
} from '@vnetwork/ui/primitives';
import { DataTable, type ColumnDef, EmptyState } from '@vnetwork/ui/data';
import { ConfirmDialog, Loading, useToast } from '@vnetwork/ui/feedback';
import {
  useVodPresetGroups,
  usePresetDetail,
  useDeletePresetGroup,
} from '@/api/media/vod-presets';
import type { Presets, PresetProfile } from '@/api/media/vod-presets-types';
import { RowMenu } from '../../components/RowMenu';
import { RefreshButton } from '../../_shared/AnalyticsKit';
import { VodConsoleBreadcrumbs } from '../VodConsoleBreadcrumbs';
import styles from './PresetGroups.module.css';

// ─── Preset detail dialog ─────────────────────────────────────────────────────

function PresetDetailDialog({
  serviceId,
  presetId,
  presetGroupId,
  onClose,
}: {
  serviceId: string;
  presetId: string;
  presetGroupId: string;
  onClose: () => void;
}) {
  const { data, isLoading } = usePresetDetail(serviceId, presetId, presetGroupId);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Preset Detail</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {isLoading ? (
            <Loading label="Loading…" size="sm" />
          ) : !data ? (
            <span className={styles.cellMono}>Preset not found.</span>
          ) : (
            <div className={styles.detailBody}>
              <div>
                <p className={styles.detailName}>{data.name}</p>
                {data.description && (
                  <p className={styles.detailDesc}>{data.description}</p>
                )}
              </div>

              <div>
                <p className={styles.detailSectionLabel}>Video</p>
                <dl className={styles.detailGrid}>
                  {[
                    ['Codec', data.video.codec],
                    ['Bitrate', `${data.video.bitrate} kbps`],
                    ['FPS', data.video.fps],
                    ['GOP', data.video.gop],
                    ['Profile', data.video.profile],
                    ['Resolution', `${data.video.width}×${data.video.height}`],
                  ].map(([k, v]) => (
                    <div key={String(k)}>
                      <dt className={styles.detailDt}>{k}</dt>
                      <dd className={styles.detailDd}>{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>

              <div>
                <p className={styles.detailSectionLabel}>Audio</p>
                <dl className={styles.detailGrid}>
                  {[
                    ['Codec', data.audio.codec],
                    ['Bitrate', `${data.audio.bitrate} kbps`],
                    ['Sample rate', `${data.audio.sample_rate} Hz`],
                    ['Channels', data.audio.channels],
                  ].map(([k, v]) => (
                    <div key={String(k)}>
                      <dt className={styles.detailDt}>{k}</dt>
                      <dd className={styles.detailDd}>{String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild><Button variant="secondary">Close</Button></DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Group detail dialog ──────────────────────────────────────────────────────
// Shows all profiles in a preset group; clicking a profile opens PresetDetailDialog.

function GroupDetailDialog({
  serviceId,
  group,
  onClose,
}: {
  serviceId: string;
  group: Presets;
  onClose: () => void;
}) {
  const [detailPreset, setDetailPreset] = useState<{ presetId: string; groupId: string } | null>(null);
  const profiles: PresetProfile[] = group.profiles ?? [];

  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{group.label || group.value}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className={styles.detailBody}>
              {group.description && (
                <p className={styles.detailDesc}>{group.description}</p>
              )}
              {profiles.length === 0 ? (
                <p className={styles.detailDesc}>No profiles in this group.</p>
              ) : (
                <div>
                  <p className={styles.detailSectionLabel}>Profiles ({profiles.length})</p>
                  <div className={styles.groupDetailGrid}>
                    {profiles.map((p) => (
                      <button
                        key={p.id}
                        className={styles.profileCard}
                        type="button"
                        onClick={() => setDetailPreset({ presetId: p.id, groupId: group.value })}
                      >
                        <p className={styles.profileName}>{p.name}</p>
                        <p className={styles.profileMeta}>{p.bitrate}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </DialogBody>
          <DialogFooter>
            <DialogClose asChild><Button variant="secondary">Close</Button></DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {detailPreset && (
        <PresetDetailDialog
          serviceId={serviceId}
          presetId={detailPreset.presetId}
          presetGroupId={detailPreset.groupId}
          onClose={() => setDetailPreset(null)}
        />
      )}
    </>
  );
}

// ─── Main list ────────────────────────────────────────────────────────────────

/** Download the given preset groups as a CSV file. */
function exportCsv(rows: Presets[]) {
  const head = ['id', 'preset_group', 'description', 'profiles'];
  const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const body = rows
    .map((g) => [g.value, g.label, g.description, g.profiles?.length ?? 0].map(esc).join(','))
    .join('\n');
  const blob = new Blob([`${head.join(',')}\n${body}`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `preset-groups-${rows.length}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function VodPresetGroups({ serviceId }: { serviceId: string }) {
  const navigate = useNavigate();
  const toast = useToast();

  const { data: groups = [], isLoading, refetch } = useVodPresetGroups(serviceId);
  const deleteMutation = useDeletePresetGroup(serviceId);

  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [detailGroup, setDetailGroup] = useState<Presets | null>(null);
  const [search, setSearch] = useState('');

  const rows = useMemo(() => {
    if (!search.trim()) return groups;
    const q = search.toLowerCase();
    return groups.filter((g) => (g.label ?? '').toLowerCase().includes(q) || (g.description ?? '').toLowerCase().includes(q));
  }, [groups, search]);

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget, {
      onSuccess: () => { toast.success('Preset group deleted'); setDeleteTarget(null); refetch(); },
      onError: (err) => { toast.error((err as Error).message || 'Failed to delete preset group'); setDeleteTarget(null); },
    });
  };

  const columns: ColumnDef<Presets, unknown>[] = [
    {
      accessorKey: 'value',
      header: 'ID',
      size: 120,
      cell: ({ row }) => <span className={styles.cellMono}>{row.original.value}</span>,
    },
    {
      accessorKey: 'label',
      header: 'Preset Group',
      size: 200,
      cell: ({ row }) => (
        <span className={styles.cellLabel}>{row.original.label || '—'}</span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <span className={styles.cellDesc}>{row.original.description || '—'}</span>
      ),
    },
    {
      id: 'profiles',
      header: 'Profiles',
      size: 120,
      cell: ({ row }) => {
        const count = row.original.profiles?.length ?? 0;
        return (
          <Badge tone="neutral" appearance="subtle" size="sm">
            {count} profile{count !== 1 ? 's' : ''}
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      size: 60,
      cell: ({ row }) => (
        <RowMenu
          items={[
            {
              label: 'Delete',
              danger: true,
              onSelect: () => setDeleteTarget(row.original.value),
            },
          ]}
        />
      ),
    },
  ];

  const loading = isLoading;

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<VodConsoleBreadcrumbs trail={[{ label: 'Transcoding Presets' }]} />}
        title={`Transcoding Presets${groups.length ? ` (${groups.length})` : ''}`}
        description="Manage transcoding preset groups and their quality profiles."
        actions={
          <Button
            variant="primary"
            startIcon={<Plus size={15} />}
            onClick={() => navigate(`/services/vod/${serviceId}/transcoding-presets/create`)}
          >
            Add Preset Group
          </Button>
        }
      />

      <Toolbar
        start={
          <Input
            placeholder="Search by name or description…"
            startSlot={<Search size={15} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            wrapperClassName={styles.searchInput}
          />
        }
        end={
          <>
            <RefreshButton onRefresh={() => void refetch()} />
            <Button
              variant="secondary"
              startIcon={<Download size={15} />}
              disabled={rows.length === 0}
              onClick={() => exportCsv(rows)}
            >
              Export
            </Button>
          </>
        }
      />

      {loading ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} variant="rect" height={44} />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          getRowId={(r) => r.value}
          onRowClick={(row) => setDetailGroup(row)}
          emptyState={
            // First-use shows a creation hint; once groups exist the empty state
            // means the search excluded everything.
            groups.length === 0 ? (
              <EmptyState
                icon={<Layers size={20} />}
                title="No preset groups"
                description="Add a preset group to configure quality profiles for encoding jobs."
                actions={
                  <Button
                    size="sm"
                    startIcon={<Plus size={14} />}
                    onClick={() => navigate(`/services/vod/${serviceId}/transcoding-presets/create`)}
                  >
                    Add Preset Group
                  </Button>
                }
              />
            ) : (
              'No preset groups match your search.'
            )
          }
          footer={groups.length ? `Showing ${rows.length} of ${groups.length} preset groups` : undefined}
        />
      )}

      {detailGroup && (
        <GroupDetailDialog
          serviceId={serviceId}
          group={detailGroup}
          onClose={() => setDetailGroup(null)}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Delete Preset Group"
        description="This will permanently delete the preset group. This action cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={deleteMutation.isPending}
        onConfirm={handleDeleteConfirm}
        requireText={deleteTarget ?? undefined}
      />
    </Container>
  );
}
